import { useState, useCallback, useEffectEvent } from 'react';
import { useCurrentClass } from 'store/classes/selectors';
import { useAllBook, useBookOrder } from 'store/books/selectors';
import { useDispatch } from 'react-redux';
import { handleListBooksByIds, handleListExamBooks, handleListExternalBooks } from 'store/books/thunks';
import { listBooksByIdsSuccess } from 'store/books/actions';
import { useCurrentUser } from 'store/users/selectors';
import { get } from 'lodash';
import { handleListAssignedActivitiesByIds, handleListCustomAssignment, listResourseActivities } from 'store/activities/thunks';
import { handleListAssignedQuizzesByIds, handleListResourseQuizzes } from 'store/quizzes/thunks';
import { listActivitiesByIdsSuccess } from 'store/activities/actions';
import { listQuizzesByIdsSuccess } from 'store/quizzes/actions';
var globalLoadingState = new Set();
var exam_books_loaded = false;
var external_books_loaded = false;
var external_books = [];
var exam_books = [];
var admin_activity_books = new Set();
var admin_quizzes_books = new Set();
var last_loaded_class_id = null;

export const useClassBooks = () => {
    const currentClass = useCurrentClass();
    const booksAllList = useAllBook();
    const dispatch = useDispatch();
    const bookOrder = useBookOrder();
    const currentUser = useCurrentUser();
    const [loading, setLoading] = useState(false);
    const [loadingExternalBooks, setLoadingExternalBooks] = useState(false);
    const [error, setError] = useState(null);
    const fetchExamBooks = useCallback(async () => {
        try {
            if (exam_books_loaded) return exam_books;
            exam_books_loaded = true;
            const examBooks = await dispatch(handleListExamBooks());
            if (examBooks && examBooks.success) {
                exam_books = examBooks.data.books || [];
                return examBooks.data.books || [];
            }
            return [];
        } catch (error) {
            console.log(error, "error in fetchExamBooks")
            return [];
        }
    }, [dispatch]);
    const fetchExternalBooks = useCallback(async () => {
        try {
            if (external_books_loaded) return external_books;
            external_books_loaded = true;
            const externalBooks = await dispatch(handleListExternalBooks());
            if (externalBooks && externalBooks.success) {
                external_books = externalBooks.data.books || [];
                return externalBooks.data.books || [];
            }
            return [];
        } catch (error) {
            console.log(error, "error in fetchExternalBooks")
            return [];
        }
    }, [dispatch,currentClass]);
    // fetch activities and quizzes for resources
    const fetchActivitiesAndQuizzesForResources = useCallback(async () => {
        const [activities, quizzes] = await Promise.all([
           dispatch(listResourseActivities()),
            dispatch(handleListResourseQuizzes())
        ]);
        let activitiesData = [];
        let quizzesData = [];
        if(activities &&activities.success) {
            activitiesData = activities.data || [];
        }
        if(quizzes && quizzes.success) {
            quizzesData = quizzes.data || [];
        }
        return { activities: activitiesData, quizzes: quizzesData };
    }, [dispatch]);
    const fetchActivitiesForBooks = useEffectEvent(async (ids) => {
        if (!currentClass?.id) {
            return [];
        }
        const chunkSize = 10;
        const chunks = [];
        const activities = [];
        const filteredIds = ids.filter(id => !admin_activity_books.has(id));
        for (let i = 0; i < filteredIds.length; i += chunkSize) {
            chunks.push(filteredIds.slice(i, i + chunkSize));
        }
        const activitiesResponse = await Promise.allSettled(chunks.map(chunk => dispatch(handleListAssignedActivitiesByIds(chunk, currentClass.id))));
        admin_activity_books.add(...filteredIds);
        activitiesResponse.forEach(response => {
            if (response.status === 'fulfilled' && response.value && response.value.success) {
                activities.push(...response.value.data);
            }
        });
        return activities;
    }, [dispatch, currentClass]);
    const fetchQuizzesForBooks = useEffectEvent(async (ids) => {
        if (!currentClass?.id) {
            return [];
        }
        const chunkSize = 10;
        const chunks = [];
        const quizzes = [];
        const filteredIds = ids.filter(id => !admin_quizzes_books.has(id));
        for (let i = 0; i < filteredIds.length; i += chunkSize) {
            chunks.push(filteredIds.slice(i, i + chunkSize));
        }
        const quizzesResponse = await Promise.allSettled(chunks.map(chunk => dispatch(handleListAssignedQuizzesByIds(chunk, currentClass.id))));
        admin_quizzes_books.add(...filteredIds);
        quizzesResponse.forEach(response => {
            if (response.status === 'fulfilled' && response.value && response.value.success) {
                quizzes.push(...response.value.data);
            }
        });
        return quizzes;
    }, [dispatch, currentClass]);
    const fetchCustomAssignmentForBooks = useCallback(async () => {
        const customAssignment = await dispatch(handleListCustomAssignment(get(currentUser, "role"), get(currentUser, "id")))
        return customAssignment;
    }, [dispatch,currentUser]);
    const fetchAssignment = useCallback(async (ids) => {
        const [activities, quizzes , customAssignment, activitiesAndQuizzesForResources] = await Promise.all([
            fetchActivitiesForBooks(ids),
            fetchQuizzesForBooks(ids),
            fetchCustomAssignmentForBooks(),
            fetchActivitiesAndQuizzesForResources()
        ]);
        const finalActivities = [];
        const finalQuizzes = [];
        if(Array.isArray(activities) && activities.length > 0) {
            finalActivities.push(...activities);
        }
        if(Array.isArray(quizzes) && quizzes.length > 0) {
            finalQuizzes.push(...quizzes);
        }
        if(Array.isArray(customAssignment) && customAssignment.length > 0) {
            finalActivities.push(...customAssignment);
        }
        if(Array.isArray(activitiesAndQuizzesForResources.activities) && activitiesAndQuizzesForResources.activities.length > 0) {
            finalActivities.push(...activitiesAndQuizzesForResources.activities);
        }
        if(Array.isArray(activitiesAndQuizzesForResources.quizzes) && activitiesAndQuizzesForResources.quizzes.length > 0) {
            finalQuizzes.push(...activitiesAndQuizzesForResources.quizzes);
        }
        dispatch(listActivitiesByIdsSuccess(finalActivities));
        dispatch(listQuizzesByIdsSuccess(finalQuizzes));
    }, [dispatch,currentUser]);

    // fetch assignment end. 
    const fetchClassBooks = useCallback(async () => {
        if (!currentClass?.books_group || currentClass.books_group.length === 0) {
            return;
        }
        try {
            const show_loading = last_loaded_class_id !== currentClass?.id;
            if(show_loading) {
                setLoading(true);
                last_loaded_class_id = currentClass?.id;
            }
            setError(null);
            let ids = [];
            let ext_books = [];
            const [examBooks, externalBooks] = await Promise.all([fetchExamBooks(), fetchExternalBooks()]);
            if (Array.isArray(examBooks) && examBooks.length > 0) {
                ext_books.push(...examBooks);
                ids.push(...examBooks.map(k => k.id));
            }
            if (Array.isArray(externalBooks) && externalBooks.length > 0) {
                ext_books.push(...externalBooks);
                ids.push(...externalBooks.map(k => k.id));
            }
            await dispatch(listBooksByIdsSuccess(ext_books));
            if (Array.isArray(bookOrder) && bookOrder.length > 0) {
                const BookItem = new Set();
                bookOrder.forEach(k => {
                    if (currentClass.books_group.includes(k.key)) {
                        k.order.forEach(id => {
                            BookItem.add(id);
                        });
                    }
                })
                if (BookItem.size > 0) {
                    let books_data = [];
                    const bookIds = Array.from(BookItem);
                    ids.push(...bookIds);
                    fetchAssignment(ids);
                    const unloadedBookIds = bookIds.filter(id => !booksAllList.find(book => book.id === id));

                    if (unloadedBookIds.length > 0) {
                        const chunkSize = 20;
                        const chunks = [];
                        for (let i = 0; i < unloadedBookIds.length; i += chunkSize) {
                            chunks.push(unloadedBookIds.slice(i, i + chunkSize));
                        }
                        const booksResponse = await Promise.allSettled(chunks.map(chunk => dispatch(handleListBooksByIds(chunk))));
                        booksResponse.forEach(response => {
                            if (response.status === 'fulfilled' && response.value && response.value.success) {
                                books_data.push(...response.value.data);
                            }
                        });
                        await dispatch(listBooksByIdsSuccess(books_data));
                    }
                }
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch class books');
            console.error('Error fetching class books:', err);
        } finally {
            setLoading(false);

        }
    }, [currentClass, booksAllList, dispatch, bookOrder]);

    const fetchLinkBooks = useCallback(async (ids) => {
        try {
            setLoadingExternalBooks(true);
            const response = await dispatch(handleListBooksByIds(ids));
            if (response && response.success && response.data && response.data.length > 0) {
                await dispatch(listBooksByIdsSuccess(response.data));
                return response.data;
            }
            return [];
        }
        catch (error) {
            console.log(error, "error in fetchLikBooks")
            return [];
        }
        finally {
            setLoadingExternalBooks(false);
        }
    }, [dispatch]);

    const clearLoadingState = () => {
        globalLoadingState = new Set();
        exam_books_loaded = false;
        external_books_loaded = false;
        external_books = [];
        exam_books = [];
        admin_activity_books = new Set();
        admin_quizzes_books = new Set();
    }

    return {
        loading,
        error,
        fetchClassBooks,
        clearLoadingState,
        fetchLinkBooks,
        loadingExternalBooks,
        fetchActivitiesForBooks,
        fetchQuizzesForBooks
    };
};
